// src/components/ManageMenuPages.js

import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  deleteDoc,
  updateDoc,
  doc as firestoreDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase";
import "./ManageMenuPages.css";

const ManageMenuPages = () => {
  // pages: array of { id, pageNumber, imagePath, url, newPageNumber }
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState(""); // For success/error messages
  const [actionError, setActionError] = useState("");

  // 1) Fetch all menuPages docs (ordered by pageNumber) and also their Storage URLs
  const fetchPages = async () => {
    setLoading(true);
    setActionMsg("");
    setActionError("");
    try {
      const pagesRef = collection(db, "menuPages");
      const q = query(pagesRef, orderBy("pageNumber", "asc"));
      const snapshot = await getDocs(q);

      const fetched = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const id = docSnap.id;
          const { pageNumber, imagePath } = data;

          // Get the download URL for the image
          let url = "";
          try {
            url = await getDownloadURL(storageRef(storage, imagePath));
          } catch (e) {
            console.warn(
              `Could not get download URL for ${imagePath}:`,
              e.message
            );
          }

          return {
            id,
            pageNumber,
            imagePath,
            url,
            newPageNumber: pageNumber, // used for editing
          };
        })
      );
      setPages(fetched);
    } catch (err) {
      console.error("Error fetching menu pages:", err);
      setActionError("Failed to load menu pages. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  // 2) Handle deleting a page: remove Storage file, then Firestore doc, then refetch
  const handleDelete = async (page) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete page #${page.pageNumber}? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    setActionMsg("");
    setActionError("");
    try {
      // 2.1) Delete the image from Storage
      const imgRef = storageRef(storage, page.imagePath);
      await deleteObject(imgRef);

      // 2.2) Delete the Firestore document
      await deleteDoc(firestoreDoc(db, "menuPages", page.id));

      setActionMsg(`Page #${page.pageNumber} deleted successfully.`);
      // 2.3) Refetch the list
      fetchPages();
    } catch (err) {
      console.error("Error deleting page:", err);
      setActionError(
        "Failed to delete this page. Check console and ensure the file exists."
      );
    }
  };

  // 3) Handle updating pageNumber
  const handleUpdateNumber = async (page) => {
    const newNum = Number(page.newPageNumber);
    if (isNaN(newNum) || newNum <= 0) {
      setActionError("Page number must be a positive number.");
      return;
    }
    if (newNum === page.pageNumber) {
      setActionError("The new page number is the same as the current one.");
      return;
    }
    setActionMsg("");
    setActionError("");
    try {
      // Update Firestore document field "pageNumber"
      await updateDoc(
        firestoreDoc(db, "menuPages", page.id),
        { pageNumber: newNum }
      );
      setActionMsg(
        `Page #${page.pageNumber} updated to #${newNum} successfully.`
      );
      // Refetch so the list reorders
      fetchPages();
    } catch (err) {
      console.error("Error updating pageNumber:", err);
      setActionError("Could not update page number. See console for details.");
    }
  };

  // 4) Handle input-change for newPageNumber
  const handleInputChange = (e, index) => {
    const value = e.target.value;
    setPages((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, newPageNumber: value } : p
      )
    );
  };

  return (
    <div className="manage-pages-container">
      <h2>Manage Menu Pages</h2>

      {actionMsg && <div className="success-text">{actionMsg}</div>}
      {actionError && <div className="error-text">{actionError}</div>}

      {loading ? (
        <div className="loading-text">Loading pages…</div>
      ) : pages.length === 0 ? (
        <div className="no-pages-text">No menu pages found.</div>
      ) : (
        <table className="pages-table">
          <thead>
            <tr>
              <th>Thumbnail</th>
              <th>Current #</th>
              <th>New # (Edit)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((page, idx) => (
              <tr key={page.id}>
                <td>
                  {page.url ? (
                    <img
                      src={page.url}
                      alt={`Page ${page.pageNumber}`}
                      className="thumbnail"
                    />
                  ) : (
                    <span className="no-thumbnail">No Image</span>
                  )}
                </td>
                <td className="current-number">{page.pageNumber}</td>
                <td>
                  <input
                    type="number"
                    min="1"
                    value={page.newPageNumber}
                    onChange={(e) => handleInputChange(e, idx)}
                    className="number-input"
                  />
                  <button
                    onClick={() => handleUpdateNumber(page)}
                    className="update-btn"
                  >
                    Update
                  </button>
                </td>
                <td>
                  <button
                    onClick={() => handleDelete(page)}
                    className="delete-btn"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ManageMenuPages;
